import { BarChart, GaugeChart } from "echarts/charts";
import { GridComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { useEffect, useRef } from "react";
import type { Locale, SiteState } from "../shared/project-state";

echarts.use([BarChart, GaugeChart, GridComponent, TooltipComponent, CanvasRenderer]);

export function ProgressCharts({ state, locale }: { state: SiteState; locale: Locale }) {
  const gaugeRef = useRef<HTMLDivElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!gaugeRef.current || !barRef.current) return;
    if (!document.createElement("canvas").getContext("2d")) return;
    const gauge = echarts.init(gaugeRef.current);
    const bar = echarts.init(barRef.current);
    gauge.setOption({
      series: [
        {
          type: "gauge",
          progress: { show: true, width: 12 },
          axisLine: { lineStyle: { width: 12 } },
          axisTick: { show: false },
          splitLine: { length: 8 },
          pointer: { width: 4 },
          detail: { formatter: "{value}%", fontSize: 22 },
          data: [{ value: state.overallProgress, name: locale === "zh-CN" ? "总体" : "Overall" }],
        },
      ],
    });
    bar.setOption({
      tooltip: {},
      grid: { left: 32, right: 18, top: 20, bottom: 26 },
      xAxis: { type: "category", data: state.stages.map((stage) => stage.id) },
      yAxis: { type: "value", max: 100 },
      series: [
        { type: "bar", data: state.stages.map((stage) => stage.progress), itemStyle: { borderRadius: 8 } },
      ],
    });
    const resize = () => {
      gauge.resize();
      bar.resize();
    };
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      gauge.dispose();
      bar.dispose();
    };
  }, [locale, state]);

  return (
    <div className="chart-grid" role="group" aria-label={locale === "zh-CN" ? "进度图表" : "Progress charts"}>
      <div ref={gaugeRef} className="chart-card" role="img" aria-label={`${state.overallProgress}%`} />
      <div ref={barRef} className="chart-card chart-card-wide" role="img" aria-label="L0-L5 progress bars" />
    </div>
  );
}
